import { useEffect } from "react";
import { useLocation, useParams, Navigate } from "react-router-dom";
import { Link } from "@/components/LangLink";
import { Home, Package, Phone, CalendarCheck } from "lucide-react";

// Vecchi URL del sito precedente → nuova destinazione equivalente.
// Redirect permanente lato client (la piattaforma non consente 301 server-side).
// URL esatti verificati con l'utente dai risultati Google ancora indicizzati.
export const LEGACY_REDIRECTS = {
  "/contattaci": "/contatti",
  "/azienda": "/chi-siamo",
  "/calze-curative": "/prodotti/schiebler",
  "/elastocompressivi/calze-curative": "/prodotti/schiebler",
  "/elastocompressivi": "/prodotti",
};

// Contenuti eliminati senza equivalente nel nuovo sito → pagina di cortesia "rimossa".
export const GONE_PATHS = [
  "/accessori",
  "/accessori-e-prodotti-monouso",
  "/accessori-prodotti-monouso",
  "/arredo-sanitario",
];

const QUICK_LINKS = [
  { to: "/", label: "Torna alla Home", icon: Home, testid: "notfound-home-link" },
  { to: "/prodotti", label: "Scopri i prodotti", icon: Package, testid: "notfound-prodotti-link" },
  { to: "/contatti", label: "Contattaci", icon: Phone, testid: "notfound-contatti-link" },
  { to: "/il-metodo#prenota", label: "Prenota una misurazione", icon: CalendarCheck, testid: "notfound-prenota-link" },
];

const NotFound = ({ gone = false }) => {
  // Titolo + noindex: applicati DOPO l'effetto SEO del Layout (timeout), ripristinati all'uscita
  useEffect(() => {
    const t = window.setTimeout(() => {
      document.title = gone ? "Pagina rimossa — Medisistem" : "Pagina non trovata — Medisistem";
      const robots = document.head.querySelector('meta[name="robots"]');
      if (robots) robots.setAttribute("content", "noindex, follow");
    }, 50);
    return () => {
      window.clearTimeout(t);
      const robots = document.head.querySelector('meta[name="robots"]');
      if (robots) robots.setAttribute("content", "index, follow, max-image-preview:large, max-snippet:-1");
    };
  }, [gone]);

  return (
    <section className="bg-white" data-testid="notfound-page">
      <div className="container-x py-24 lg:py-32 max-w-2xl text-center">
        <p className="eyebrow justify-center" data-testid="notfound-eyebrow">{gone ? "Contenuto rimosso" : "Errore 404"}</p>
        <h1 className="mt-4 font-serif text-navy text-4xl sm:text-5xl leading-tight" data-testid="notfound-title">
          {gone ? "Questa pagina è stata rimossa." : "Questa pagina non esiste."}
        </h1>
        <p className="mt-5 text-ink/60 leading-relaxed" data-testid="notfound-text">
          {gone
            ? "Il contenuto che cercavi non fa più parte del nostro catalogo online. Qui sotto trovi le pagine principali del nuovo sito."
            : "L'indirizzo potrebbe essere stato digitato male, oppure la pagina è stata spostata. Qui sotto trovi le pagine principali del sito."}
        </p>
        <div className="mt-10 grid sm:grid-cols-2 gap-3">
          {QUICK_LINKS.map((l) => (
            <Link
              key={l.testid}
              to={l.to}
              data-testid={l.testid}
              className="flex items-center justify-center gap-2.5 rounded-full border border-[#E7EDF2] px-6 py-3.5 text-sm font-semibold text-navy transition-colors hover:border-green hover:text-green"
            >
              <l.icon size={16} className="text-green" /> {l.label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Gestisce tutti gli URL sconosciuti: redirect se esiste un equivalente, altrimenti pagina 404/410.
export const LegacyOrNotFound = () => {
  const { pathname } = useLocation();
  const { lang } = useParams();
  const clean = pathname.replace(/^\/[a-z]{2}(?=\/)/i, "").replace(/\/+$/, "").toLowerCase() || "/";
  const target = LEGACY_REDIRECTS[clean];
  if (target) return <Navigate to={`/${lang}${target}`} replace />;
  return <NotFound gone={GONE_PATHS.includes(clean)} />;
};

export default NotFound;
